import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { getUniversityBySlug } from '../data/universityData';
import { CheckCircleIcon, ChevronLeftIcon, BuildingIcon, GraduationCapIcon, AwardIcon, PhoneIcon, LockIcon, ArrowLeftRightIcon, FileCheckIcon, TimerIcon, HelpCircleIcon } from 'lucide-react';
const iconMap: Record<string, React.ElementType> = {
  ArrowLeftRightIcon,
  FileCheckIcon,
  TimerIcon,
  AwardIcon
};
export function BrandedCollegePage() {
  const {
    collegeSlug
  } = useParams<{
    collegeSlug: string;
  }>();
  const navigate = useNavigate();
  const university = collegeSlug ? getUniversityBySlug(collegeSlug) : undefined;
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const totalSteps = 4;
  const [formData, setFormData] = useState({
    degreeLevel: '',
    program: '',
    militaryAffiliation: '',
    highestEducation: '',
    hsGradYear: '',
    startDate: '',
    zipCode: '',
    city: '',
    state: '',
    streetAddress: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    consent: false
  });
  const currentYear = new Date().getFullYear();
  const years = Array.from({
    length: 60
  }, (_, i) => currentYear - i);
  useEffect(() => {
    // Auto-lookup zip code
    if (formData.zipCode.length === 5) {
      fetch(`https://api.zippopotam.us/us/${formData.zipCode}`).then(res => {
        if (res.ok) return res.json();
        throw new Error('Invalid zip');
      }).then(data => {
        if (data.places && data.places.length > 0) {
          setFormData(prev => ({
            ...prev,
            city: data.places[0]['place name'],
            state: data.places[0]['state abbreviation']
          }));
        }
      }).catch(() => {
        // Handle error silently
      });
    }
  }, [formData.zipCode]);
  if (!university) {
    return <div className="min-h-[60vh] flex items-center justify-center bg-gray-50" data-id="element-698">
        <div className="text-center" data-id="element-699">
          <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-700">
            University Not Found
          </h2>
          <p className="text-gray-600 mb-6" data-id="element-701">
            We couldn't find the college you're looking for.
          </p>
          <button onClick={() => navigate('/')} className="bg-[#3b82f6] text-white px-6 py-2.5 rounded-lg font-bold hover:bg-[#2563eb] transition-colors" data-id="element-702">
            Return Home
          </button>
        </div>
      </div>;
  }
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  const handleZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 5);
    setFormData(prev => ({
      ...prev,
      zipCode: value
    }));
  };
  const nextStep = () => {
    setDirection(1);
    setStep(s => Math.min(s + 1, totalSteps));
  };
  const prevStep = () => {
    setDirection(-1);
    setStep(s => Math.max(s - 1, 1));
  };
  const handleSubmit = () => {
    if (formData.consent) {
      setIsSubmitted(true);
      window.scrollTo(0, 0);
    }
  };
  const slideVariants = {
    enter: (dir: number) => ({
      x: dir > 0 ? 50 : -50,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (dir: number) => ({
      x: dir < 0 ? 50 : -50,
      opacity: 0
    })
  };
  // Filter programs based on selected degree level
  const filteredPrograms = university.programs.filter(p => !formData.degreeLevel || p.degreeLevel === formData.degreeLevel);
  // Group programs by category for the bottom section
  const programsByCategory = university.programs.reduce((acc, program) => {
    if (!acc[program.category]) {
      acc[program.category] = [];
    }
    acc[program.category].push(program.name);
    return acc;
  }, {} as Record<string, string[]>);
  if (isSubmitted) {
    return <div className="min-h-screen bg-gray-50 flex flex-col" data-id="element-703">
        {/* Top Header Bar */}
        <div className="w-full bg-white border-b border-gray-200 py-4 px-4 flex justify-between items-center max-w-7xl mx-auto" data-id="element-704">
          {university.logoUrl ? <img src={university.logoUrl} alt={university.name} className="h-10 object-contain" data-id="element-705" /> : <div className="text-xl font-bold text-gray-900" data-id="element-706">
              {university.logoText}
            </div>}
          {university.phoneNumber && <div className="flex items-center gap-2 text-gray-700 font-medium" data-id="element-707">
              <PhoneIcon className="w-5 h-5" data-id="element-708" />
              <span data-id="element-709">{university.phoneNumber}</span>
            </div>}
        </div>
        <div className="flex-grow flex items-center justify-center p-4" data-id="element-710">
          <div className="bg-white rounded-2xl shadow-xl p-8 max-w-lg w-full text-center" data-id="element-711">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-712">
              <CheckCircleIcon className="w-10 h-10 text-green-500" data-id="element-713" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-714">
              Thank You!
            </h2>
            <p className="text-gray-600 mb-6 text-lg" data-id="element-715">
              Your request for information about{' '}
              <strong data-id="element-716">{formData.program}</strong> at{' '}
              <strong data-id="element-717">{university.name}</strong> has been submitted.
            </p>
            <p className="text-gray-600 mb-8" data-id="element-718">
              A representative from {university.name} will contact you shortly
              to discuss your educational goals.
            </p>

            <button onClick={() => navigate('/')} className="w-full py-3.5 text-white font-bold rounded-full text-lg transition-opacity hover:opacity-90" style={{
            backgroundColor: university.ctaColor
          }} data-id="element-719">
              Return Home
            </button>
          </div>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-white font-sans" data-id="element-720">
      {/* Header Bar */}
      <div className="w-full bg-white border-b border-gray-200 py-3 px-4 md:px-8 flex justify-between items-center" data-id="element-721">
        {university.logoUrl ? <img src={university.logoUrl} alt={university.name} className="h-10 object-contain" data-id="element-722" /> : <div className="text-xl font-bold text-gray-900" data-id="element-723">
            {university.logoText}
          </div>}
        {university.phoneNumber && <div className="flex items-center gap-1.5 text-gray-600 font-medium text-lg" data-id="element-724">
            <PhoneIcon className="w-5 h-5" data-id="element-725" />
            <span data-id="element-726">{university.phoneNumber}</span>
          </div>}
      </div>

      {/* Hero Section */}
      <div className="relative w-full min-h-[600px] flex items-center" data-id="element-727">
        {/* Background Image */}
        <div className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat" style={{
        backgroundImage: `url(${university.heroImageUrl})`
      }} data-id="element-728" />

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 py-12 md:py-20 grid lg:grid-cols-2 gap-8 items-center" data-id="element-729">
          {/* Left: Tagline */}
          <div className="hidden lg:block" data-id="element-730">
            <div className="bg-black/60 p-6 inline-block" data-id="element-731">
              <h1 className="text-white text-4xl md:text-5xl font-serif italic leading-tight max-w-md" data-id="element-732">
                {university.heroTagline}
              </h1>
            </div>
          </div>

          {/* Right: Form Card */}
          <div className="bg-white shadow-2xl w-full max-w-md mx-auto lg:ml-auto lg:mr-0 flex flex-col" data-id="element-733">
            {/* Form Header */}
            <div className="bg-[#1a1a1a] text-center py-6 px-4" data-id="element-734">
              <div className="text-red-600 font-bold tracking-wider text-sm mb-1 uppercase" data-id="element-735">
                Classes Start {university.classesStartDate}
              </div>
              <h2 className="text-white text-2xl md:text-3xl font-bold uppercase tracking-wide" data-id="element-736">
                Take the First Step Today
              </h2>
            </div>

            {/* Step Indicators - 4 steps */}
            <div className="flex w-full" data-id="element-737">
              {[{
              num: 1,
              label: 'Program',
              sub: 'Selection'
            }, {
              num: 2,
              label: 'Education',
              sub: 'Details'
            }, {
              num: 3,
              label: 'Location',
              sub: 'Info'
            }, {
              num: 4,
              label: 'Contact',
              sub: 'Info'
            }].map(s => <div key={s.num} className={`flex-1 py-2.5 px-1 flex items-center justify-center gap-1.5 ${step === s.num ? 'bg-black text-white' : step > s.num ? 'bg-gray-300 text-gray-700' : 'bg-gray-200 text-gray-500'}`} data-id="element-738">
                  <div className={`w-6 h-6 flex items-center justify-center border-2 text-sm font-bold ${step === s.num ? 'border-white' : step > s.num ? 'border-gray-500' : 'border-gray-400'}`} data-id="element-739">
                    {step > s.num ? '✓' : s.num}
                  </div>
                  <div className="text-[10px] leading-tight hidden sm:block" data-id="element-740">
                    <span className="font-bold block uppercase" data-id="element-741">{s.label}</span>
                    <span className="capitalize" data-id="element-742">{s.sub}</span>
                  </div>
                </div>)}
            </div>

            {/* Form Body */}
            <div className="p-6 md:p-8 bg-[#f5f4f0] flex-grow" data-id="element-743">
              <p className="text-gray-600 italic text-sm mb-4" data-id="element-744">
                All fields required
              </p>

              <div className="overflow-hidden" data-id="element-745">
                <AnimatePresence mode="wait" custom={direction} data-id="element-746">
                  {/* Step 1: Program Selection */}
                  {step === 1 && <motion.div key="step1" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
                  duration: 0.3
                }} className="space-y-4" data-id="element-747">
                      <div data-id="element-748">
                        <select name="degreeLevel" value={formData.degreeLevel} onChange={handleInputChange} className="w-full px-4 py-3 bg-white border border-gray-300 text-gray-700 focus:outline-none focus:ring-1 focus:ring-red-600" data-id="element-749">
                          <option value="" data-id="element-750">Select a Degree Level*</option>
                          {university.degreeLevels.map((level, i) => <option key={i} value={level} data-id="element-751">
                              {level}
                            </option>)}
                        </select>
                      </div>

                      <div data-id="element-752">
                        <select name="program" value={formData.program} onChange={handleInputChange} disabled={!formData.degreeLevel} className="w-full px-4 py-3 bg-white border border-gray-300 text-gray-700 focus:outline-none focus:ring-1 focus:ring-red-600 disabled:bg-gray-100 disabled:text-gray-400" data-id="element-753">
                          <option value="" data-id="element-754">Choose your degree program*</option>
                          {filteredPrograms.map((p, i) => <option key={i} value={p.name} data-id="element-755">
                              {p.name}
                            </option>)}
                        </select>
                      </div>

                      <div className="pt-2" data-id="element-756">
                        <label className="flex items-center gap-1 text-sm font-bold text-gray-900 mb-2" data-id="element-757">
                          *Are You Affiliated with the Military?
                          <HelpCircleIcon className="w-4 h-4 text-gray-500" data-id="element-758" />
                        </label>
                        <div className="flex gap-6" data-id="element-759">
                          <label className="flex items-center gap-2 cursor-pointer" data-id="element-760">
                            <input type="radio" name="militaryAffiliation" value="Yes" checked={formData.militaryAffiliation === 'Yes'} onChange={handleInputChange} className="w-5 h-5 text-red-600 focus:ring-red-600" data-id="element-761" />
                            <span className="text-gray-900 font-medium" data-id="element-762">
                              Yes
                            </span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer" data-id="element-763">
                            <input type="radio" name="militaryAffiliation" value="No" checked={formData.militaryAffiliation === 'No'} onChange={handleInputChange} className="w-5 h-5 text-red-600 focus:ring-red-600" data-id="element-764" />
                            <span className="text-gray-900 font-medium" data-id="element-765">
                              No
                            </span>
                          </label>
                        </div>
                      </div>

                      <div className="pt-6" data-id="element-766">
                        <button onClick={nextStep} type="button" disabled={!formData.degreeLevel || !formData.program || !formData.militaryAffiliation} className="w-full py-4 text-white font-bold text-lg uppercase tracking-wider transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed" style={{
                      backgroundColor: university.ctaColor
                    }} data-id="element-767">
                          Go To Next Step
                        </button>
                        <div className="flex items-center justify-center gap-2 mt-4 text-gray-600 text-sm" data-id="element-768">
                          <LockIcon className="w-4 h-4" data-id="element-769" />
                          <span data-id="element-770">We respect your privacy</span>
                        </div>
                      </div>
                    </motion.div>}

                  {/* Step 2: Education Details */}
                  {step === 2 && <motion.div key="step2" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
                  duration: 0.3
                }} className="space-y-4" data-id="element-771">
                      <div data-id="element-772">
                        <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2" data-id="element-773">
                          Highest Level of Education*
                        </label>
                        <select name="highestEducation" value={formData.highestEducation} onChange={handleInputChange} className="w-full px-4 py-3 bg-white border border-gray-300 text-gray-700 focus:outline-none focus:ring-1 focus:ring-red-600" data-id="element-774">
                          <option value="" data-id="element-775">Please Select</option>
                          <option value="Some High School" data-id="element-776">
                            Some High School
                          </option>
                          <option value="High School/GED" data-id="element-777">
                            High School/GED
                          </option>
                          <option value="Some College" data-id="element-778">Some College</option>
                          <option value="Associate's Degree" data-id="element-779">
                            Associate's Degree
                          </option>
                          <option value="Bachelor's Degree" data-id="element-780">
                            Bachelor's Degree
                          </option>
                          <option value="Master's Degree" data-id="element-781">
                            Master's Degree
                          </option>
                          <option value="Doctorate" data-id="element-782">Doctorate</option>
                        </select>
                      </div>

                      <div data-id="element-783">
                        <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2" data-id="element-784">
                          High School Graduation Year*
                        </label>
                        <select name="hsGradYear" value={formData.hsGradYear} onChange={handleInputChange} className="w-full px-4 py-3 bg-white border border-gray-300 text-gray-700 focus:outline-none focus:ring-1 focus:ring-red-600" data-id="element-785">
                          <option value="" data-id="element-786">Please Select</option>
                          {years.map(year => <option key={year} value={year} data-id="element-787">
                              {year}
                            </option>)}
                        </select>
                      </div>

                      <div data-id="element-788">
                        <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2" data-id="element-789">
                          When Would You Like to Start?*
                        </label>
                        <select name="startDate" value={formData.startDate} onChange={handleInputChange} className="w-full px-4 py-3 bg-white border border-gray-300 text-gray-700 focus:outline-none focus:ring-1 focus:ring-red-600" data-id="element-790">
                          <option value="" data-id="element-791">Please Select</option>
                          <option value="Immediately" data-id="element-792">Immediately</option>
                          <option value="1-3 Months" data-id="element-793">1-3 Months</option>
                          <option value="3-6 Months" data-id="element-794">3-6 Months</option>
                          <option value="6-12 Months" data-id="element-795">6-12 Months</option>
                          <option value="1+ Year" data-id="element-796">1+ Year</option>
                        </select>
                      </div>

                      <div className="flex gap-4 pt-6" data-id="element-797">
                        <button onClick={prevStep} type="button" className="px-5 py-3 border border-gray-400 font-bold text-gray-700 flex items-center gap-1 hover:bg-gray-100 transition-colors uppercase text-sm tracking-wider" data-id="element-798">
                          <ChevronLeftIcon className="w-4 h-4" data-id="element-799" />
                          Back
                        </button>
                        <button onClick={nextStep} type="button" disabled={!formData.highestEducation || !formData.hsGradYear || !formData.startDate} className="flex-1 py-3 text-white font-bold text-lg uppercase tracking-wider transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed" style={{
                      backgroundColor: university.ctaColor
                    }} data-id="element-800">
                          Next Step
                        </button>
                      </div>
                    </motion.div>}

                  {/* Step 3: Location */}
                  {step === 3 && <motion.div key="step3" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
                  duration: 0.3
                }} className="space-y-4" data-id="element-801">
                      <div data-id="element-802">
                        <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2" data-id="element-803">
                          Zip Code*
                        </label>
                        <input type="text" inputMode="numeric" name="zipCode" value={formData.zipCode} onChange={handleZipChange} maxLength={5} placeholder="e.g. 90210" className="w-full px-4 py-3 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-1 focus:ring-red-600" data-id="element-804" />
                      </div>

                      <div className="flex gap-4" data-id="element-805">
                        <div className="flex-1" data-id="element-806">
                          <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2" data-id="element-807">
                            City*
                          </label>
                          <input type="text" name="city" value={formData.city} onChange={handleInputChange} className="w-full px-4 py-3 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-1 focus:ring-red-600" data-id="element-808" />
                        </div>
                        <div className="flex-1" data-id="element-809">
                          <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2" data-id="element-810">
                            State*
                          </label>
                          <input type="text" name="state" value={formData.state} onChange={handleInputChange} className="w-full px-4 py-3 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-1 focus:ring-red-600" data-id="element-811" />
                        </div>
                      </div>

                      <div data-id="element-812">
                        <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2" data-id="element-813">
                          Street Address{' '}
                          <span className="text-gray-400 normal-case font-normal" data-id="element-814">
                            (optional)
                          </span>
                        </label>
                        <input type="text" name="streetAddress" value={formData.streetAddress} onChange={handleInputChange} className="w-full px-4 py-3 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-1 focus:ring-red-600" data-id="element-815" />
                      </div>

                      <div className="flex gap-4 pt-6" data-id="element-816">
                        <button onClick={prevStep} type="button" className="px-5 py-3 border border-gray-400 font-bold text-gray-700 flex items-center gap-1 hover:bg-gray-100 transition-colors uppercase text-sm tracking-wider" data-id="element-817">
                          <ChevronLeftIcon className="w-4 h-4" data-id="element-818" />
                          Back
                        </button>
                        <button onClick={nextStep} type="button" disabled={!formData.zipCode || !formData.city || !formData.state} className="flex-1 py-3 text-white font-bold text-lg uppercase tracking-wider transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed" style={{
                      backgroundColor: university.ctaColor
                    }} data-id="element-819">
                          Next Step
                        </button>
                      </div>
                    </motion.div>}

                  {/* Step 4: Contact Info */}
                  {step === 4 && <motion.div key="step4" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
                  duration: 0.3
                }} className="space-y-4" data-id="element-820">
                      <div className="flex gap-4" data-id="element-821">
                        <div className="flex-1" data-id="element-822">
                          <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2" data-id="element-823">
                            First Name*
                          </label>
                          <input type="text" name="firstName" value={formData.firstName} onChange={handleInputChange} className="w-full px-4 py-3 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-1 focus:ring-red-600" data-id="element-824" />
                        </div>
                        <div className="flex-1" data-id="element-825">
                          <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2" data-id="element-826">
                            Last Name*
                          </label>
                          <input type="text" name="lastName" value={formData.lastName} onChange={handleInputChange} className="w-full px-4 py-3 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-1 focus:ring-red-600" data-id="element-827" />
                        </div>
                      </div>

                      <div data-id="element-828">
                        <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2" data-id="element-829">
                          Email Address*
                        </label>
                        <input type="email" name="email" value={formData.email} onChange={handleInputChange} className="w-full px-4 py-3 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-1 focus:ring-red-600" data-id="element-830" />
                      </div>

                      <div data-id="element-831">
                        <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2" data-id="element-832">
                          Phone Number*
                        </label>
                        <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} className="w-full px-4 py-3 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-1 focus:ring-red-600" data-id="element-833" />
                      </div>

                      <div className="flex items-start gap-3 mt-4" data-id="element-834">
                        <input type="checkbox" id="consent" name="consent" checked={formData.consent} onChange={handleInputChange} className="mt-1 w-4 h-4 text-red-600 border-gray-300 focus:ring-red-600" data-id="element-835" />
                        <label htmlFor="consent" className="text-[10px] text-gray-600 leading-tight" data-id="element-836">
                          By clicking the Request Info button, you agree to be
                          contacted by phone, text message, or email via
                          automated systems by {university.name} about your
                          education at the phone numbers you provided above,
                          including any wireless number(s). You are not
                          obligated to agree to automated contact to enroll.
                          Note that even non-automated calls are recorded for
                          quality assurance.
                        </label>
                      </div>

                      <div className="flex gap-4 pt-6" data-id="element-837">
                        <button onClick={prevStep} type="button" className="px-5 py-3 border border-gray-400 font-bold text-gray-700 flex items-center gap-1 hover:bg-gray-100 transition-colors uppercase text-sm tracking-wider" data-id="element-838">
                          <ChevronLeftIcon className="w-4 h-4" data-id="element-839" />
                          Back
                        </button>
                        <button onClick={handleSubmit} type="button" disabled={!formData.firstName || !formData.lastName || !formData.email || !formData.phone || !formData.consent} className="flex-1 py-3 text-white font-bold text-lg uppercase tracking-wider transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed" style={{
                      backgroundColor: university.ctaColor
                    }} data-id="element-840">
                          Request Info
                        </button>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Row */}
      <div className="bg-[#f5f5f5] py-16 border-b border-gray-200" data-id="element-841">
        <div className="max-w-7xl mx-auto px-4" data-id="element-842">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12" data-id="element-843">
            {university.features?.map((feature, idx) => {
            const Icon = iconMap[feature.icon] || CheckCircleIcon;
            return <div key={idx} className="flex flex-col items-center text-center" data-id="element-844">
                  <div className="mb-6" data-id="element-845">
                    <Icon className="w-16 h-16 text-gray-700 stroke-1" data-id="element-846" />
                  </div>
                  <h3 className="text-xl font-serif font-bold text-black mb-3" data-id="element-847">
                    {feature.label}
                    {feature.superscript && <sup className="text-xs ml-0.5" data-id="element-848">
                        {feature.superscript}
                      </sup>}
                  </h3>
                  <p className="text-gray-700 text-sm" data-id="element-849">
                    {feature.description}
                    {feature.superscript && <sup className="text-[10px] ml-0.5" data-id="element-850">
                        {feature.superscript}
                      </sup>}
                  </p>
                </div>;
          })}
          </div>
        </div>
      </div>

      {/* Testimonial Section */}
      {university.testimonial && <div className="w-full py-20 px-4" style={{
      backgroundColor: university.primaryColor
    }} data-id="element-851">
          <div className="max-w-5xl mx-auto flex flex-col md:flex-row gap-8 items-start" data-id="element-852">
            <div className="text-white text-9xl font-serif leading-none h-20 opacity-90" data-id="element-853">
              "
            </div>
            <div className="flex-1 pt-4" data-id="element-854">
              <p className="text-white text-xl md:text-2xl font-serif italic leading-relaxed mb-6" data-id="element-855">
                {university.testimonial.quote}
              </p>
              <div className="text-right" data-id="element-856">
                <div className="text-white font-bold text-lg" data-id="element-857">
                  {university.testimonial.name}
                </div>
                <div className="text-white/90 italic text-sm" data-id="element-858">
                  {university.testimonial.program}
                </div>
              </div>
            </div>
          </div>
        </div>}

      {/* Mobile/Access Section */}
      {university.mobileSection && <div className="w-full py-20 bg-white" data-id="element-859">
          <div className="max-w-6xl mx-auto px-4 grid md:grid-cols-2 gap-12 items-center" data-id="element-860">
            <div data-id="element-861">
              <img src="https://images.unsplash.com/photo-1512314889357-e157c22f938d?w=800&h=600&fit=crop&q=80" alt="Student using mobile phone" className="w-full h-auto shadow-lg" data-id="element-862" />
            </div>
            <div className="space-y-6" data-id="element-863">
              <h2 className="text-3xl md:text-4xl font-serif text-[#991B1B]" data-id="element-864">
                {university.mobileSection.heading}
              </h2>
              <p className="text-gray-700 leading-relaxed" data-id="element-865">
                {university.mobileSection.description}
              </p>
              <button className="px-8 py-3 text-white font-bold text-lg transition-colors border-2 hover:bg-white hover:text-[#B91C1C]" style={{
            backgroundColor: university.ctaColor,
            borderColor: university.ctaColor
          }} onClick={() => window.scrollTo({
            top: 0,
            behavior: 'smooth'
          })} data-id="element-866">
                Request Info
              </button>
            </div>
          </div>
        </div>}

      {/* University Details */}
      <div className="max-w-7xl mx-auto px-4 py-16 border-t border-gray-200" data-id="element-867">
        <div className="grid md:grid-cols-3 gap-8 mb-16" data-id="element-868">
          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm flex items-start gap-4" data-id="element-869">
            <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0" data-id="element-870">
              <BuildingIcon className="w-6 h-6 text-blue-600" data-id="element-871" />
            </div>
            <div data-id="element-872">
              <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-1" data-id="element-873">
                Institution Type
              </h4>
              <p className="text-lg font-semibold text-gray-900" data-id="element-874">
                {university.type}
              </p>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm flex items-start gap-4" data-id="element-875">
            <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center flex-shrink-0" data-id="element-876">
              <AwardIcon className="w-6 h-6 text-green-600" data-id="element-877" />
            </div>
            <div data-id="element-878">
              <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-1" data-id="element-879">
                Accreditation
              </h4>
              <p className="text-lg font-semibold text-gray-900" data-id="element-880">
                {university.accreditation}
              </p>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm flex items-start gap-4" data-id="element-881">
            <div className="w-12 h-12 rounded-full bg-purple-50 flex items-center justify-center flex-shrink-0" data-id="element-882">
              <GraduationCapIcon className="w-6 h-6 text-purple-600" data-id="element-883" />
            </div>
            <div data-id="element-884">
              <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-1" data-id="element-885">
                Founded
              </h4>
              <p className="text-lg font-semibold text-gray-900" data-id="element-886">
                {university.founded}
              </p>
            </div>
          </div>
        </div>

        {/* Programs Grid */}
        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center" data-id="element-887">
          Available Online Programs
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-888">
          {Object.entries(programsByCategory).map(([category, programs]) => <div key={category} className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-889">
              <h3 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100" data-id="element-890">
                {category}
              </h3>
              <ul className="space-y-2" data-id="element-891">
                {programs.map((program, idx) => <li key={idx} className="flex items-start gap-2 text-gray-700" data-id="element-892">
                    <CheckCircleIcon className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" data-id="element-893" />
                    <span data-id="element-894">{program}</span>
                  </li>)}
              </ul>
            </div>)}
        </div>
      </div>

      {/* Footer Separator */}
      <div className="w-full h-1 bg-[#991B1B]" data-id="element-895"></div>
    </div>;
}